import _ from 'lodash';
import { formatSeasonEpisode, formatSeasonEpisodeExtended } from 'helpers/VideoHelper';
import { isContentAuthorized } from 'components/AuthClientless/AuthHelper';
import { getDmaFromVideo } from 'helpers/AuthHelper';
import {
  formatUpcomingDate,
  getDisplayBrand,
  getLiveMetaData,
  getMovieMetadata,
  getNetworkLogoUrl,
  getOriginalAirdateMetaData,
  getRequiresAuth,
  isChannelType,
  isClip,
  isEpisode,
  isLive,
  isShowcase,
  isShowcaseCountdown,
  isSportsContent,
  isTypeCategory,
  isTypePromo,
  isTypeSeries,
} from 'helpers/ContentHelper';
import {
  COLLECTION_HERO_LOGO_IMAGE,
  getStillBackgroundImage,
  getDynamicImage,
} from 'helpers/ImageHelper';
import {
  helperGetRelativeUrl,
  helperGetLiveUrl,
  helperGetSeriesDetailUrl,
} from 'helpers/RouteHelper';
import { getAuthNetworksConfig } from 'helpers/ConfigHelper';
import { SITE_PLATFORM_MOBILE } from 'helpers/BreakpointHelper';
import {
  now,
  stringToDate,
  isBetween,
  isAfter,
  isBefore,
  differenceInSeconds,
} from 'helpers/DateTimeHelper';
import { PROMO_TYPE_MARKETING_VIDEO } from 'helpers/SeriesDetailHelpers';

const getItemData = item => {
  const { '@type': type, videos } = item;
  if (isTypePromo(type)) {
    const description = _.get(item, 'description', null) || _.get(videos, '0.description', '');
    return {
      ...item,
      ..._.get(videos, '0', {}),
      autoPlayStill: _.get(videos, '0.autoPlayStill', null),
      autoPlayVideo: _.get(videos, '0.autoPlayVideo', null),
      description,
      images: _.get(videos, '0.images', null),
      headline: _.get(item, 'headline', null),
      isCustomDescription: !!_.get(item, 'description', null),
      isPromoContent: true,
      promoBackgroundImage: _.get(item, 'images.promoImage', null),
      promoBackgroundVideo: _.get(item, 'videos.0.autoPlayVideo.default.url', null),
      promoSeriesScreenUrl: _.get(item, 'videos[0].seriesScreenUrl', null),
      promoPlayerScreenUrl: _.get(item, 'videos[0].playerScreenUrl', null),
    };
  }
  return item;
};

export const featuredVideoProcessor = ({ item: videoEntry, breakpoint, context, categoryType }) => {
  if (!videoEntry) {
    return null;
  }
  const item = getItemData(videoEntry);

  const {
    '@id': url,
    '@type': type,
    id,
    actors,
    callSign,
    categoryType: typeCategory,
    collectionType,
    contentRating,
    description,
    headline,
    alternativeHeadline,
    displaySeasonAndEpisodeCounts,
    durationInSeconds,
    endDate,
    episodeNumber,
    eventData,
    fullEpisodeCount,
    images,
    isAuthenticated,
    isCustomDescription,
    isPromoContent,
    isSeriesDetailAvailable,
    isUserOutOfMarket,
    livePlayerScreenUrl,
    name,
    network,
    oldestEpisode,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
    preauthorizedResources,
    promoBackgroundImage,
    promoBackgroundVideo,
    promoSeriesScreenUrl,
    promoPlayerScreenUrl,
    releaseYear,
    screenUrl,
    seasonCount,
    seasonNumber,
    secondaryTitle,
    seriesName,
    seriesScreenUrl,
    seriesType,
    showCode,
    startDate,
    subRatings,
    tabIndex,
    videoId,
    videoType,
  } = item;

  const { badge_liveText: liveText, badge_upcomingText: upcomingText } = window.Language;

  const displayBrand = getDisplayBrand(item);
  const isCategory = isTypeCategory(type);
  const isCountdown = isShowcaseCountdown(item);
  const isChannelItem = isCategory && isChannelType(typeCategory);
  const requiresAuth = getRequiresAuth({ video: item, isLive: !!livePlayerScreenUrl });
  let logoType = null;

  if (isCategory) {
    logoType = 'logo-tab-alt';
  } else {
    logoType = breakpoint === SITE_PLATFORM_MOBILE ? 'logoCenter' : 'logo';
  }

  const logo = _.get(images, logoType, _.get(images, 'logo'));

  const fallbackImage = _.get(images, 'still.raw');
  const image = _.get(images, 'raw', fallbackImage);
  const imageUrl = getDynamicImage({ breakpoint, context, url: image });
  const authNetworksConfig = getAuthNetworksConfig();
  const isAuthorized = requiresAuth
    ? isContentAuthorized(preauthorizedResources, [network], authNetworksConfig)
    : true;
  const fadeButtons = !isAuthorized && isAuthenticated;

  let badge;
  if (categoryType) item.categoryType = categoryType;

  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const seriesLogo = getDynamicImage({
    breakpoint,
    context: COLLECTION_HERO_LOGO_IMAGE,
    url: _.get(logo, 'raw', null),
  });
  const videoBackgroundImage = _.get(item, 'autoPlayVideo.default.url', null);
  const stillBackgroundImage = getStillBackgroundImage({ item });
  let seriesUrl = seriesScreenUrl;
  if (isChannelItem) {
    seriesUrl = url.replace('categories', 'channel');
  }
  let titleLink = helperGetRelativeUrl({
    url: isPromoContent ? promoSeriesScreenUrl : seriesUrl,
  });
  let subtitle;
  let seriesPlayerScreenUrl;
  let previewPlayerScreenUrl;
  let seriesSeasonEpisode;
  let isOnAir;

  const metadata = formatUpcomingDate(startDate);
  const upcomingMetadata = formatUpcomingDate(startDate);
  const liveMetaData = getLiveMetaData({ startDate, durationInSeconds });
  const movieMetaData = getMovieMetadata({
    releaseYear,
    durationInSeconds,
  });
  const clipMetaData = getMovieMetadata({ durationInSeconds });
  const currentSeason =
    _.get(item, 'currentSeason.seasonNumber') || _.get(item, 'oldestEpisode.seasonNumber');
  const seasonEpisodeMetaData = formatSeasonEpisodeExtended(
    seasonCount,
    currentSeason,
    fullEpisodeCount,
  );
  const originalAirDateMetaData = getOriginalAirdateMetaData({
    originalAirDate,
    durationInSeconds,
  });
  if ((isEpisode(videoType) || isClip(videoType)) && !isCustomDescription) {
    subtitle = secondaryTitle || name;
    subtitle = `${displaySeasonAndEpisodeCounts ? seasonEpisode : ''} ${subtitle}`;
  }
  if (isTypeSeries(type)) {
    titleLink = helperGetSeriesDetailUrl(showCode);
    seriesPlayerScreenUrl = _.get(oldestEpisode, 'playerScreenUrl');
    const seriesEpisodeNumber = _.get(oldestEpisode, 'episodeNumber');
    const seriesSeasonNumber = _.get(oldestEpisode, 'seasonNumber');
    seriesSeasonEpisode = formatSeasonEpisode(seriesSeasonNumber, seriesEpisodeNumber);
  }
  const watchLink = helperGetRelativeUrl({
    url: isPromoContent
      ? promoPlayerScreenUrl || promoSeriesScreenUrl
      : playerScreenUrl || seriesScreenUrl || seriesPlayerScreenUrl,
  });
  const previewLink = helperGetRelativeUrl({ url: previewPlayerScreenUrl });

  const isSportingEvent = isSportsContent(item);
  let secUntilStart = null;
  let liveVideoEnded = false;
  if (isLive(type)) {
    const currentDate = now();
    const startsAt = stringToDate(startDate);
    const endsAt = stringToDate(endDate);
    const isUpcoming = isAfter(startsAt, currentDate);
    isOnAir = isBetween(currentDate, startsAt, endsAt);
    if (isUpcoming) {
      secUntilStart = differenceInSeconds(startsAt, currentDate);
      badge = upcomingText;
    } else {
      badge = liveText;
    }
    if (isCountdown) {
      badge = liveText;
    }
    if (!watchLink || isSportingEvent) {
      titleLink = isUpcoming ? null : helperGetLiveUrl({ callSign, videoId });
    }
    if (isAfter(currentDate, endsAt)) liveVideoEnded = true;
  }

  const videoData = {
    actors,
    badge,
    callSign,
    clipMetaData,
    collectionType,
    contentRating,
    description,
    headline,
    alternativeHeadline,
    descriptionLines: 2,
    displaySeasonAndEpisodeCounts,
    durationInSeconds,
    endDate,
    episodeNumber,
    eventData,
    fadeButtons,
    fullEpisodeCount,
    id,
    image: imageUrl,
    isLive: !!livePlayerScreenUrl,
    isOnAir,
    isSeriesDetailAvailable,
    isUserOutOfMarket,
    link: helperGetRelativeUrl({ url: screenUrl }),
    liveMetaData,
    livePlayerScreenUrl,
    liveVideoEnded,
    metadata,
    movieMetaData,
    name,
    network,
    networkIcon,
    originalAirDateMetaData,
    originalVideo: item,
    percentWatched,
    playButton: true,
    playerScreenUrl,
    previewLink,
    promoBackgroundImage,
    promoBackgroundVideo,
    releaseYear,
    requiresAuth,
    seasonCount,
    seasonEpisodeMetaData,
    seasonNumber,
    secUntilStart,
    seriesLogo,
    seriesName,
    seriesSeasonEpisode,
    seriesType,
    showCode,
    startDate,
    stillBackgroundImage,
    subRatings,
    subtitle,
    tabIndex,
    title: seriesName || name,
    titleLink,
    type,
    upcomingMetadata,
    videoBackgroundImage,
    videoId,
    videoType,
    watchLink,
  };

  return videoData;
};

export const addSeeMoreItem = ({ items, collectionCTA, isFirst }) => {
  const url = _.get(collectionCTA, '0.url');
  if (!url || isFirst) return items;
  const label = _.get(collectionCTA, '0.label', 'See All');
  const seeMoreItem = {
    '@type': 'seeMore',
    id: 'seeMore',
    headline: label,
    link: collectionCTA,
  };
  return _.concat(items, seeMoreItem);
};

export const getCollectionOrder = panels => {
  if (!panels) return null;
  const collections = _.get(panels, 'member');
  return _.map(_.compact(collections), item => item.id);
};

export function shouldRenderDefaultPanel(panel, updateCollectionOrder) {
  const items = _.get(panel, 'items', null);
  if (!panel || !items) {
    updateCollectionOrder(panel);
    return false;
  }
  if (isShowcase(panel)) updateCollectionOrder(panel);
  return true;
}

export const getCollectionIndex = (collectionIds, currentPanelId) =>
  _.findIndex(collectionIds, id => id === currentPanelId) + 1;

export const getCollectionName = pathname => {
  const collections = _.get(window.ExternalConfig, 'navigation', []);
  const rootCollection = _.find(collections, { link: '/' });
  let collectionData = null;
  if (pathname === '/' && rootCollection) {
    collectionData = rootCollection;
  } else {
    collectionData = _.find(collections, c => c.link !== '/' && _.includes(pathname, c.link));
  }
  return _.get(collectionData, 'label');
};

export const setShowcaseVideoDma = ({ showcase, dma }) => {
  const showcaseVideo = _.get(showcase, 'items.member.0');
  const showcaseVideoDma = getDmaFromVideo(showcaseVideo);
  if (!dma || showcaseVideoDma) return showcase;

  _.set(showcaseVideo, 'trackingData.context.location.dma', dma);
  const showcaseItems = _.get(showcase, 'items', {});
  const panel = {
    ...showcase,
    items: {
      ...showcaseItems,
      member: [showcaseVideo],
    },
  };
  return panel;
};

const isAvailable = availableDate => {
  if (!availableDate) return true;

  const currentDateTime = now();
  return !isBefore(currentDateTime, stringToDate(availableDate));
};

const isExpired = expirationDate => {
  if (!expirationDate) return false;

  const currentDateTime = now();
  return !isBefore(currentDateTime, stringToDate(expirationDate));
};

const getShowcases = panels => _.filter(panels, panel => isShowcase(panel));

const isTimelyShowcase = (panel = {}) => !!panel.availableDate || !!panel.expirationDate;

const getNonTimelyShowcases = panels => _.filter(panels, panel => !isTimelyShowcase(panel));

const getTimelyShowcases = panels => _.filter(panels, panel => isTimelyShowcase(panel));

// NOTE: this only supports showcases with availableDate and expirationDate provided
// Do not pass in non-timely showcases
const getCurrentTimelyShowcase = panels =>
  _.find(panels, panel => isAvailable(panel.availableDate) && !isExpired(panel.expirationDate));

export const getCurrentShowcase = panels => {
  const showcases = getShowcases(panels);
  const timelyShowcases = getTimelyShowcases(showcases);
  if (timelyShowcases.length) {
    const currentShowcase = getCurrentTimelyShowcase(timelyShowcases);
    if (currentShowcase) return currentShowcase;
  }
  const nonTimelyShowcases = getNonTimelyShowcases(showcases);
  return _.first(nonTimelyShowcases) || null;
};

const getNextAvailableShowcase = panels => {
  const currentTime = now();
  const showcases = getShowcases(panels);
  const timelyShowcases = getTimelyShowcases(showcases);
  const futureShowcases = _.filter(
    timelyShowcases,
    showcase =>
      showcase.availableDate && isAfter(stringToDate(showcase.availableDate), currentTime),
  );
  return _.first(futureShowcases.sort((a, b) => a.availableDate.localeCompare(b.availableDate)));
};

export const getTimeoutTime = (currentShowcase, panels) => {
  const currentExpirationDate = _.get(currentShowcase, 'expirationDate');
  if (currentExpirationDate) return currentExpirationDate;
  const nextShowcase = getNextAvailableShowcase(panels);
  return _.get(nextShowcase, 'availableDate', null);
};

export const didPanelsAvailabilityChange = (prevPanels, panels) => {
  // check if any previous showcases are no longer being sent to us
  const prevShowcases = getShowcases(prevPanels);
  const prevShowcaseRemoved = _.some(prevShowcases, p => !_.find(panels, { id: p.id }));
  if (prevShowcaseRemoved) return true;

  // check if new showcases are available OR their available/expiration dates changed
  const showcases = getShowcases(panels);
  return _.some(showcases, panel => {
    const { id, availableDate, expirationDate } = panel;
    const prevPanel = _.find(prevShowcases, p => p.id === id);
    if (!prevPanel) return true;
    const { availableDate: prevAvailableDate, expirationDate: prevExpirationDate } = prevPanel;
    const availabilityChanged = prevAvailableDate !== availableDate;
    const expirationChanged = prevExpirationDate !== expirationDate;
    return availabilityChanged || expirationChanged;
  });
};

export const getMarketingVideoBanner = panel => {
  if (!panel || !panel.items || !panel.items.member) return false;
  const videoBanner = _.get(panel.items.member, '0', {});
  return videoBanner.promoType === PROMO_TYPE_MARKETING_VIDEO && videoBanner;
};
